import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Shield, User as UserIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "cashier" });

  const load = async () => {
    const { data } = await api.get("/users");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Usuario creado");
      setOpen(false);
      setForm({ email: "", password: "", name: "", role: "cashier" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error al crear");
    }
  };

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Eliminado");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Error");
    }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="users-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-clay font-semibold">Acceso</div>
          <h1 className="font-outfit text-4xl font-bold text-stone">Usuarios</h1>
          <p className="text-stone/60 mt-1">Administradores y cajeros del sistema.</p>
        </div>
        <Button
          onClick={() => setOpen(true)}
          data-testid="user-new-button"
          className="bg-clay hover:bg-clay-hover text-white shadow-[0_4px_14px_rgba(200,90,64,0.39)] hover:-translate-y-0.5 transition-all"
        >
          <Plus className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-warm-border overflow-hidden shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone/50">
              <th className="px-6 py-3 font-semibold">Usuario</th>
              <th className="px-6 py-3 font-semibold">Correo</th>
              <th className="px-6 py-3 font-semibold">Rol</th>
              <th className="px-6 py-3 font-semibold text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id} className="border-t border-warm-border hover:bg-sand/60 transition-colors">
                <td className="px-6 py-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-bone font-semibold ${u.role === "admin" ? "bg-clay" : "bg-stone"}`}>
                      {u.name?.charAt(0).toUpperCase() || "?"}
                    </div>
                    <div className="font-medium text-stone">{u.name}</div>
                  </div>
                </td>
                <td className="px-6 py-3 text-sm text-stone/70">{u.email}</td>
                <td className="px-6 py-3">
                  <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-wider px-2.5 py-1 rounded-md ${
                    u.role === "admin" ? "bg-clay/15 text-clay" : "bg-sage/15 text-[#5C7363]"
                  }`}>
                    {u.role === "admin" ? <Shield className="w-3 h-3" /> : <UserIcon className="w-3 h-3" />}
                    {u.role === "admin" ? "Admin" : "Cajero"}
                  </span>
                </td>
                <td className="px-6 py-3 text-right">
                  {u.id !== me?.id && (
                    <button
                      onClick={() => del(u)}
                      data-testid={`user-delete-${u.id}`}
                      className="p-2 rounded-md hover:bg-clay/10 text-stone/60 hover:text-clay"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-bone border-warm-border">
          <DialogHeader>
            <DialogTitle className="font-outfit text-2xl">Nuevo usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">Nombre</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="user-name-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">Correo</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="user-email-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">Contraseña</label>
              <Input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-password-input"
                className="border-warm-border bg-white focus-visible:ring-clay focus-visible:ring-offset-0"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-stone/70 mb-1.5 block">Rol</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="border-warm-border bg-white" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cashier">Cajero</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="border-warm-border">Cancelar</Button>
            <Button onClick={create} data-testid="user-save-button" className="bg-clay hover:bg-clay-hover text-white">
              Crear usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
